// Shared filename helper for chapter- and section-level PDF exports.
//
// Locked spec:
//   Chapter:  ClaimGuard_<FileNumber>_Chapter_<NN>_<ChapterTitle>.pdf
//   Section:  ClaimGuard_<FileNumber>_Chapter_<NN>_Section_<SectionTitle>.pdf
//
// Registry-driven callers should prefer `buildChapterPdfFilenameFromEntry`
// and `buildSectionPdfFilenameFromEntry` so the chapter number and title
// token always originate from the Master Chapter Registry.
//
// All parts are underscore-separated. Titles are sanitized to
// [A-Za-z0-9_] with consecutive separators collapsed and leading/trailing
// separators trimmed. Chapter numbers are zero-padded to two digits.

import type { ChapterEntry } from "./chapter-registry";


export function sanitizeFilenameToken(v: string | null | undefined): string {
  if (!v) return "";
  return String(v)
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// File-number token preserves internal hyphens (e.g. INV-2026-763463).
// Other invalid filesystem characters collapse to a single underscore,
// and leading/trailing separators are trimmed.
export function sanitizeFileNumberToken(v: string | null | undefined): string {
  if (!v) return "";
  return String(v)
    .replace(/[^A-Za-z0-9-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/-+/g, "-")
    .replace(/^[_-]+|[_-]+$/g, "");
}

export function padChapterNumber(n: string | number | null | undefined): string {
  if (n === null || n === undefined) return "00";
  const raw = String(n);
  const digits = raw.replace(/\D+/g, "");
  if (!digits) return sanitizeFilenameToken(raw) || "00";
  return digits.padStart(2, "0");
}

export function buildChapterPdfFilenameBase(
  fileNumber: string | null | undefined,
  chapterNumber: string | number | null | undefined,
  chapterTitle: string | null | undefined,
): string {
  const file = sanitizeFileNumberToken(fileNumber) || "REPORT";
  const chap = padChapterNumber(chapterNumber);
  const title = sanitizeFilenameToken(chapterTitle) || "Chapter";
  return `ClaimGuard_${file}_Chapter_${chap}_${title}`;
}

export function buildSectionPdfFilenameBase(
  fileNumber: string | null | undefined,
  chapterNumber: string | number | null | undefined,
  sectionTitle: string | null | undefined,
): string {
  const file = sanitizeFileNumberToken(fileNumber) || "REPORT";
  const chap = padChapterNumber(chapterNumber);
  const title = sanitizeFilenameToken(sectionTitle) || "Section";
  return `ClaimGuard_${file}_Chapter_${chap}_Section_${title}`;
}

/**
 * Registry-driven chapter filename. Uses the entry's zero-padded
 * displayNumber and its canonical pdfSlug so every export matches
 * the reading order automatically.
 */
export function buildChapterPdfFilenameFromEntry(
  fileNumber: string | null | undefined,
  entry: ChapterEntry,
): string {
  const file = sanitizeFileNumberToken(fileNumber) || "REPORT";
  return `ClaimGuard_${file}_Chapter_${entry.paddedDisplayNumber}_${entry.pdfSlug}`;
}

/**
 * Registry-driven section filename. Section titles are still sanitized
 * from raw strings because they represent arbitrary sub-headings.
 */
export function buildSectionPdfFilenameFromEntry(
  fileNumber: string | null | undefined,
  entry: ChapterEntry,
  sectionTitle: string | null | undefined,
): string {
  const file = sanitizeFileNumberToken(fileNumber) || "REPORT";
  const title = sanitizeFilenameToken(sectionTitle) || "Section";
  return `ClaimGuard_${file}_Chapter_${entry.paddedDisplayNumber}_Section_${title}`;
}

/**
 * Chapter-only export filename: `ClaimGuard_<Chapter_Name>` (no file number,
 * no chapter number). Used by the per-chapter Print / Download actions so a
 * standalone chapter export is named after the chapter itself, e.g.
 * `ClaimGuard_Market_Benchmark_Analysis.pdf`.
 */
export function buildChapterOnlyPdfFilenameBase(
  chapterTitle: string | null | undefined,
): string {
  const title = sanitizeFilenameToken(chapterTitle) || "Chapter";
  return `ClaimGuard_${title}`;
}

